import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  closeModal,
  initialQueryState,
  openModal,
  showToastMessage,
  updateQueryStateTable,
  useTableStore,
} from 'enable-ui';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { MODAL_KEY } from 'modules/auth/core/modal-key';

import type { Courses } from '../type';

import { trimValue } from 'helpers';
import { setUrlParams } from 'helpers/url';
import { useNumber, useString } from 'hooks';
import { apiCall } from 'hooks/service';
import { useUpdateQueryState } from 'hooks/url';
import { cloneDeep, debounce, isUndefined } from 'lodash';

import { SUCCESS_MESSAGE } from 'constants/message';
import { PATH } from 'constants/path';
import { TABLE_KEY } from 'constants/table';

export const useCoursesController = () => {
  const navigationTo = useNavigate();

  const queryState = useTableStore((s) => s.state.queryState[TABLE_KEY.COURSES]);

  const userProfile = useAuthStore(userProfileSelector);

  const params = useParams();

  const { institutionId } = params;

  useUpdateQueryState(TABLE_KEY.COURSES, 12);

  const [coursesData, setCoursesData] = useState<Courses[]>([]);

  const [selectedCourses, setSelectedCourses] = useState<Record<string, string>>({});

  const searchCourse = useString('');

  const courseSelectedId = useString('');

  const total = useNumber(0);

  useEffect(() => {
    if (!userProfile?.id) {
      return;
    }

    if (queryState) {
      setUrlParams(queryState);

      searchCourse.setValue(queryState.search ?? '');

      getListCourses();
    }
  }, [JSON.stringify(queryState), userProfile]);

  useEffect(() => {
    updateQueryStateTable(TABLE_KEY.COURSES, {
      ...initialQueryState,
      limit: 12,
    });

    return () => {
      updateQueryStateTable(TABLE_KEY.COURSES, {
        ...initialQueryState,
        limit: 12,
      });
    };
  }, []);

  const getListCourses = async () => {
    const result: {
      items: Courses[];
      total: number;
    } = await apiCall({
      url: '/courses',
      method: 'GET',
      isLoading: true,
      showError: true,
      data: {
        ...queryState,
        institutionId: userProfile?.institution?.id ?? institutionId,
      },
    });

    if (result?.items) {
      setCoursesData(result?.items ?? []);
    }

    if (result?.total) {
      total.setValue(result.total);
    }
  };

  const onChangeSelectedCourses = (course: string) => () => {
    const newSelected = cloneDeep(selectedCourses);

    if (!isUndefined(newSelected[course])) {
      delete newSelected[course];
    } else {
      newSelected[course] = course;
    }

    setSelectedCourses(newSelected);
  };

  const onChangeSearchCourse = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchCourse.setValue(event.target.value);
    debounceSearch(event.target.value);
  };

  const debounceSearch = useCallback(
    debounce((newSearch: string) => {
      updateQueryStateTable(TABLE_KEY.COURSES, {
        ...queryState,
        search: trimValue(newSearch),
        page: 1,
      });
    }, 500),
    [],
  );

  const goToCourseDetail = (courseId: string) => {
    navigationTo(PATH.EDIT_COURSES.replace(':id', courseId));
  };

  const handleEditCourse = (courseId: string) => () => {
    navigationTo(PATH.EDIT_COURSES.replace(':id', courseId));
  };

  const handleDeleteCourse = (courseId: string) => async () => {
    openModal(MODAL_KEY.DELETE_COURSE);
    courseSelectedId.setValue(courseId);
  };

  const callbackFirstPage = () => {
    updateQueryStateTable(TABLE_KEY.COURSES, {
      ...initialQueryState,
      page: 1,
      limit: 12,
    });
  };

  const onDeleteCourse = async () => {
    const response: { id: string } = await apiCall({
      url: `/courses/${courseSelectedId.value}`,
      method: 'DELETE',
      isLoading: true,
      showError: true,
    });

    if (response?.id) {
      callbackFirstPage();
    }

    getListCourses();
    setSelectedCourses({});
    closeModal(MODAL_KEY.DELETE_COURSE);
    showToastMessage('success', SUCCESS_MESSAGE.DELETE_COURSE);
  };

  const handleDelete = async () => {
    const result: string[] = [];

    for (const el of Object.keys(selectedCourses)) {
      const response: { id: string } = await apiCall({
        url: `/courses/${selectedCourses[el]}`,
        method: 'DELETE',
        isLoading: true,
        showError: true,
      });

      if (response?.id) {
        result.push(response.id);
        callbackFirstPage();
      }
    }

    if (result.length === Object.keys(selectedCourses).length) {
      showToastMessage('success', SUCCESS_MESSAGE.DELETE_COURSES);
    }

    setSelectedCourses({});
    getListCourses();
  };

  const handleDuplicateCourse = (courseId: string) => async () => {
    const result: Courses = await apiCall({
      url: `/courses/${courseId}/duplicate`,
      method: 'POST',
      isLoading: true,
      showError: true,
    });

    if (!result?.id) {
      return;
    }

    showToastMessage('success', 'Course duplicated successfully!');

    getListCourses();
  };

  return {
    coursesData,
    total,
    queryState,
    selectedCourses,
    setSelectedCourses,
    onChangeSelectedCourses,
    searchCourse,
    onChangeSearchCourse,
    goToCourseDetail,
    handleEditCourse,
    handleDeleteCourse,
    onDeleteCourse,
    handleDelete,
    handleDuplicateCourse,
  };
};
